import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Loader2, Sparkles, AlertTriangle, ShieldAlert, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// VocaLab Icon Component
const VocaLabIcon = ({ className = "h-12 w-12" }) => (
  <img 
    src="/vocalab-icon.png"
    alt="VocaLab"
    className={`${className} vocalab-icon rounded-xl`}
  />
);

// License Block Banner Component
const LicenseBlockBanner = ({ reason, message, contactEmail, onDismiss }) => {
  const getIcon = () => {
    switch (reason) {
      case 'sync_required':
        return <RefreshCw className="h-8 w-8 text-amber-400" />;
      case 'trial_expired':
      case 'license_expired':
        return <ShieldAlert className="h-8 w-8 text-red-400" />;
      default:
        return <AlertTriangle className="h-8 w-8 text-amber-400" />;
    }
  };

  const getTitle = () => {
    switch (reason) {
      case 'sync_required':
        return 'Online Sync Required';
      case 'trial_expired':
        return 'Trial Period Expired';
      case 'license_expired':
        return 'License Expired';
      case 'license_tampered':
        return 'Security Violation Detected';
      case 'hardware_mismatch':
        return 'Hardware Mismatch';
      case 'license_invalid':
        return 'Invalid License';
      case 'not_initialized':
        return 'System Not Initialized';
      default:
        return 'Access Restricted';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="max-w-md w-full mx-4">
        <Card className="premium-card border-red-500/30 bg-slate-900/95">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-3 rounded-full bg-red-500/20">
              {getIcon()}
            </div>
            <CardTitle className="text-xl font-bold text-white">
              {getTitle()}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-300 text-center text-sm">
              {message}
            </p>
            
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-400 text-center">
                For assistance, contact your school administrator or email:
                <br />
                <a href={`mailto:${contactEmail}`} className="text-cyan-400 hover:underline">
                  {contactEmail}
                </a>
              </p>
            </div>

            <Button 
              onClick={onDismiss}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [licenseBlock, setLicenseBlock] = useState(null);
  const { login, isAuthenticated, user, school } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      navigateByRole(user.role);
    }
  }, [isAuthenticated, user]);

  const navigateByRole = (role) => {
    switch (role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'teacher':
        navigate('/teacher');
        break;
      case 'student':
        // Request fullscreen for students
        requestFullscreen();
        navigate('/student');
        break;
      default:
        navigate('/');
    }
  };

  const requestFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch(err => {
        console.log('Fullscreen request failed:', err);
        // Toast notification for better UX
        setTimeout(() => {
          toast.info('💡 Tip: Use the fullscreen button in the top-right corner for focused learning', {
            duration: 5000
          });
        }, 1000);
      });
    } else if (elem.webkitRequestFullscreen) { // Safari
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { // IE11
      elem.msRequestFullscreen();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    setLicenseBlock(null);
    
    try {
      const userData = await login(email, password);
      toast.success(`Welcome, ${userData.name}!`);
      navigateByRole(userData.role);
    } catch (err) {
      // Check if this is a license block error
      const errorDetail = err.response?.data?.detail;
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.type === 'license_blocked') {
        // Show license block banner
        setLicenseBlock({
          reason: errorDetail.reason,
          message: errorDetail.message,
          contactEmail: errorDetail.contact_email || 'support@vksolutions.store'
        });
      } else {
        // Regular error
        const message = typeof errorDetail === 'string' 
          ? errorDetail 
          : (errorDetail?.message || 'Login failed. Please check your credentials.');
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex premium-bg">
      {/* License Block Banner */}
      {licenseBlock && (
        <LicenseBlockBanner
          reason={licenseBlock.reason}
          message={licenseBlock.message}
          contactEmail={licenseBlock.contactEmail}
          onDismiss={() => setLicenseBlock(null)}
        />
      )}

      {/* Left side - Gradient with content */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-violet-500/20 to-fuchsia-500/20 animate-gradient" />
        
        {/* Floating elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-cyan-500/30 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-40 right-20 w-48 h-48 bg-violet-500/30 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-fuchsia-500/30 rounded-full blur-2xl animate-pulse-glow" style={{ animationDelay: '2s' }} />
        
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="flex items-center gap-4 mb-8">
            <VocaLabIcon className="h-16 w-16" />
            <div>
              <h2 className="text-2xl font-bold premium-gradient-text">English VocaLab</h2>
              <p className="text-violet-300 flex items-center gap-1">
                <Sparkles className="h-4 w-4" />
                Powered by AI
              </p>
            </div>
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
            <span className="premium-gradient-text">Empower</span> Your<br />
            English Learning<br />
            Journey
          </h1>
          
          <p className="text-lg text-slate-300 max-w-md leading-relaxed">
            Where kids enjoy learning like playing! Interactive lab modules make English learning fun and engaging through AI-powered exercises.
          </p>

          {/* Features */}
          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <div className="w-2 h-2 rounded-full bg-cyan-400" />
              </div>
              <span className="text-slate-300">Text to Speech Practice</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
              </div>
              <span className="text-slate-300">Speech Recognition</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
              </div>
              <span className="text-slate-300">MCQ Assessments</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <div className="w-2 h-2 rounded-full bg-purple-400" />
              </div>
              <span className="text-slate-300">Cinematic MCQ</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <VocaLabIcon className="h-12 w-12" />
            <div>
              <h2 className="text-xl font-bold premium-gradient-text">English VocaLab</h2>
              <p className="text-violet-400 text-sm">Powered by AI</p>
            </div>
          </div>

          <Card className="premium-card border-white/10">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-white">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-slate-400">
                Sign in to continue to your dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email / Student ID</Label>
                  <Input
                    id="email"
                    type="text"
                    placeholder="Enter email or student ID (e.g., 2A01)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="h-11 bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus:border-violet-500"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="h-11 pr-10 bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus:border-violet-500"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 btn-gradient text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              {/* System Info */}
              <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs font-medium text-violet-400 mb-2">Welcome to English VocaLab</p>
                <div className="space-y-1 text-xs text-slate-400">
                  <p><span className="text-cyan-400">For Admins:</span> Manage school, teachers, and classes</p>
                  <p><span className="text-emerald-400">For Teachers:</span> Create lessons and track student progress</p>
                  <p><span className="text-amber-400">For Students:</span> Learn English through AI-powered exercises</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-500 mt-6">
            Powered by AI • English VocaLab © 2024
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
